import React from 'react';
import { money, pct } from '../utils/format';
import { ICONS } from './ui';

// Shared read-only rendering of a job's posting task lines, used by both the
// Create Invoices wizard (wrapped with its Bill + "To invoice" entry columns)
// and the Invoice Approval entry screen (read-only). Keeping the data columns
// and formatters here means the two screens never drift apart.

// ---- formatters / predicates ----
// money / pct are the app-wide formatters (utils/format), re-exported for the
// screens that import them from here. hours differs from utils/format: 0 shows
// as "0" and at most one decimal.
export { money, pct };
export function hours(v) {
  const n = parseFloat(v);
  if (isNaN(n) || n === 0) return '0';
  return n.toLocaleString('en-US', { maximumFractionDigits: 1 });
}
export function isLumpSum(line) {
  return String(line.TaskType || '').toLowerCase() === 'lump sum';
}
export function isUnit(line) {
  return !!line.UnitBilling;
}
// Units already placed on contract (billing) lines for a unit-billing task.
export function unitsBilled(line) {
  return (Number(line.BudgetUnits) || 0) - (Number(line.UnitsRemaining) || 0);
}

// ---- shared style tokens ----
export const th = { textAlign: 'left', padding: '6px 8px', fontSize: 12, color: 'var(--gray-600)', fontWeight: 600 };
export const thR = { ...th, textAlign: 'right' };
export const td = { padding: '6px 8px', fontSize: 14, verticalAlign: 'top' };
export const tdR = { ...td, textAlign: 'right' };
export const linkBtn = { background: 'none', border: 'none', color: 'var(--primary, #2563eb)', cursor: 'pointer', padding: 0, font: 'inherit', textDecoration: 'underline' };

// Number of read-only data columns (for colSpan on detail rows).
export function taskDataColumnCount({ showTotalBudget } = {}) {
  return showTotalBudget ? 8 : 7;
}

// The read-only column HEADERS (Task .. Rate Sheet Total). A parent table adds
// its own leading/trailing columns (Bill / To invoice) around these.
// showTotalBudget adds a "Total Budget" column before "Budget Rem." (Invoice
// Approval); Create Invoices leaves it off.
export function TaskDataHeaders({ showTotalBudget } = {}) {
  return (
    <>
      <th style={th}>Task</th>
      <th style={th}>Description</th>
      <th style={th}>Type</th>
      <th style={thR}>Approx Hrs (Last Mo.)</th>
      <th style={thR}>Unbilled</th>
      {showTotalBudget && <th style={thR}>Total Budget</th>}
      <th style={thR}>Budget Rem.</th>
      <th style={thR}>Rate Sheet Total</th>
    </>
  );
}

// The read-only column CELLS for one task row `t`. onViewEntries(jobNo, taskNo)
// and onOpenBudget(jobNo) are optional links; when omitted those render as plain
// text. jobNo is needed only for the links. Total Budget (when shown) is the
// task's Schedule (Total Price), or budget units on a unit-billing line.
// entriesInType puts "View entries" inline in the Type cell (Invoice Approval)
// instead of on its own line under the task no.
export function TaskDataCells({ t, jobNo, onViewEntries, onOpenBudget, showTotalBudget, entriesInType }) {
  const viewEntriesBtn = onViewEntries && (
    <button type="button" style={{ ...linkBtn, fontSize: 11 }}
      onClick={() => onViewEntries(String(jobNo).trim(), String(t.JobTaskNo).trim())}>
      View entries
    </button>
  );
  return (
    <>
      <td style={td}>
        {t.JobTaskNo}
        {Number(t.TaskClosed) === 1 && (
          <span style={{ marginLeft: 6, fontSize: 10, background: 'var(--gray-200)', color: 'var(--gray-700)', borderRadius: 8, padding: '0 6px' }}>closed</span>
        )}
        {viewEntriesBtn && !entriesInType && <div>{viewEntriesBtn}</div>}
      </td>
      <td style={td}>{t.Description}</td>
      <td style={entriesInType ? { ...td, whiteSpace: 'nowrap' } : td}>
        {t.UnitBilling ? `${t.TaskType || ''} (unit)` : t.TaskType}
        {viewEntriesBtn && entriesInType && <>{' '}{viewEntriesBtn}</>}
      </td>
      <td style={tdR}>{t.ApproxHours == null ? '' : hours(t.ApproxHours)}</td>
      <td style={tdR}>
        {isUnit(t)
          ? <span style={{ color: 'var(--gray-500)', fontSize: 12 }}>{hours(unitsBilled(t))} units billed</span>
          : isLumpSum(t)
            ? <span style={{ color: 'var(--gray-500)', fontSize: 12 }}>prev. {pct(t.PreviouslyInvoicedPct)}</span>
            : money(t.UnbilledAmount)}
      </td>
      {showTotalBudget && (
        <td style={tdR}>{isUnit(t) ? `${hours(t.BudgetUnits)} units` : money(t.ScheduleTotalPrice)}</td>
      )}
      <td style={tdR}>
        {onOpenBudget ? (
          <button type="button" style={linkBtn} title="Open this job's budget on the Budget tab"
            onClick={() => onOpenBudget(String(jobNo).trim())}>
            {isUnit(t) ? `${hours(t.UnitsRemaining)} units` : money(t.BudgetRemaining)}
          </button>
        ) : (isUnit(t) ? `${hours(t.UnitsRemaining)} units` : money(t.BudgetRemaining))}
      </td>
      <td style={tdR}>{isLumpSum(t) ? money(t.RateSheetTotal) : ''}</td>
    </>
  );
}

// Summarized time behind a task line: one row per (employee, note), hours summed.
// Basis is "Unbilled" for T&M lines and "PriorMonth" for lump-sum lines.
export function TimeDetailBlock({ rows, open, onToggle }) {
  const basis = rows[0] && rows[0].Basis;
  const label = basis === 'PriorMonth' ? 'Prior-month time' : 'Unbilled time';
  const sorted = [...rows].sort((a, b) => (Number(b.Hours) || 0) - (Number(a.Hours) || 0) || String(a.ResourceNo).localeCompare(String(b.ResourceNo)));
  const total = sorted.reduce((s, r) => s + (Number(r.Hours) || 0), 0);
  return (
    <div style={{ fontSize: 12, color: 'var(--gray-700)' }}>
      <button type="button" onClick={onToggle} style={{ ...linkBtn, fontSize: 12 }}>
        {open ? ICONS.collapse : ICONS.expand} {label} — {hours(total)} hrs
      </button>
      {open && (
        <ul style={{ margin: '4px 0 2px 0', padding: '0 0 0 18px', listStyle: 'none' }}>
          {sorted.map((r, i) => (
            <li key={`${r.ResourceNo}|${i}`} style={{ padding: '1px 0' }}>
              <span style={{ color: 'var(--gray-900)', fontWeight: 600 }}>{hours(r.Hours)} hrs</span>
              {'  —  '}
              <span title={r.ResourceName || ''}>{r.ResourceNo}</span>
              {r.Notes ? <span style={{ color: 'var(--gray-600)' }}>{'  —  '}{r.Notes}</span> : <span style={{ color: 'var(--gray-400)' }}>{'  —  '}(no note)</span>}
              {Number(r.EntryCount) > 1 ? <span style={{ color: 'var(--gray-400)' }}> ({r.EntryCount} entries)</span> : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
